import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bot, ChevronLeft, MessageSquarePlus, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { ThemeProvider } from '@components/Theme/ThemeProvider';
import { ToastContainer } from '@components/common/Toast';
import { UserMenu } from '@components/Layout/UserMenu';
import { MessageList } from '@components/Chat/MessageList';
import { ChatInput } from '@components/Chat/ChatInput';
import { useChatConfirm, useChatCancel } from '@api/hooks/useChatConfirm';
import { useChatCapabilities } from '@api/hooks/useChatCapabilities';
import { useChatHistoryHydration, useChatSessions, useNewChatSession } from '@api/hooks/useChatSession';
import { useChatWebSocket } from '@api/hooks/useChatWebSocket';
import { useChatStore } from '@store/chatStore';
import { brand } from '@/config/brand';
import { formatShortDate, formatTime, parseDateValue } from '@utils/formatters';

function formatConversationDate(value: string) {
  const date = parseDateValue(value);
  if (!date) return '—';
  const today = new Date();
  const isToday = date.toDateString() === today.toDateString();
  return isToday
    ? formatTime(date)
    : formatShortDate(date);
}

export const ChatPage = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const { data: sessions = [], refetch: refetchSessions } = useChatSessions();
  // Prime the backend-owned card catalogue for this workspace. This keeps the
  // full-screen Chat surface on the same capability contract as the command
  // footer and avoids treating routes/modals as a presentation fallback.
  useChatCapabilities(true);
  const history = useChatHistoryHydration(Boolean(selectedSessionId), selectedSessionId);
  const newSession = useNewChatSession();
  const { sendMessage } = useChatWebSocket();
  const {
    messages,
    isStreaming,
    resolvedConfirmationIds,
    markConfirmationResolved,
  } = useChatStore();
  const confirmMutation = useChatConfirm();
  const cancelMutation = useChatCancel();

  useEffect(() => {
    if (!selectedSessionId && sessions.length > 0) {
      setSelectedSessionId(sessions.find((session) => session.status === 'active')?.id ?? sessions[0].id);
    }
  }, [selectedSessionId, sessions]);

  useEffect(() => {
    if (!isStreaming && messages.length > 0) {
      void refetchSessions();
    }
  }, [isStreaming, messages.length, refetchSessions]);

  const handleNewChat = useCallback(() => {
    newSession.mutate(undefined, {
      onSuccess: ({ session_id }) => {
        setSelectedSessionId(session_id);
        void refetchSessions();
      },
    });
  }, [newSession, refetchSessions]);

  const handleSend = useCallback((message: string) => {
    sendMessage(message, selectedSessionId);
    void refetchSessions();
  }, [refetchSessions, selectedSessionId, sendMessage]);

  const handleConfirm = useCallback((actionId: string) => {
    confirmMutation.mutate(actionId, {
      onSuccess: () => markConfirmationResolved(actionId),
    });
  }, [confirmMutation, markConfirmationResolved]);

  const handleCancel = useCallback((actionId: string) => {
    cancelMutation.mutate(actionId, {
      onSuccess: () => markConfirmationResolved(actionId),
    });
  }, [cancelMutation, markConfirmationResolved]);

  return (
    <div className="flex h-screen overflow-hidden bg-surface-page">
      <ThemeProvider />
      <aside
        className={`flex shrink-0 flex-col border-r border-border-default bg-surface-base transition-[width,transform] duration-200 ${
          sidebarOpen ? 'w-[18rem]' : 'w-0 -translate-x-full overflow-hidden border-r-0'
        }`}
      >
        <div className="flex items-center justify-between px-4 py-4">
          <Link to="/" className="flex min-w-0 items-center gap-2 text-content-primary">
            <img src={brand.logoUrl} alt={`${brand.name} logo`} className="h-7 w-7 object-contain" />
            <span className="truncate text-sm font-semibold">{brand.name}</span>
          </Link>
          <button type="button" onClick={() => setSidebarOpen(false)} className="rounded-lg p-2 text-content-muted hover:bg-surface-raised hover:text-content-primary" aria-label="Hide session history">
            <PanelLeftClose className="h-4 w-4" />
          </button>
        </div>

        <div className="px-3 pb-3">
          <button type="button" onClick={handleNewChat} disabled={newSession.isPending} className="btn btn-primary w-full justify-start px-3">
            <MessageSquarePlus className="h-4 w-4" />
            New chat
          </button>
        </div>

        <nav className="min-h-0 flex-1 overflow-y-auto px-2 pb-3" aria-label="Chat session history">
          <p className="px-2 pb-2 text-xs font-medium uppercase tracking-wide text-content-muted">Recent chats</p>
          <div className="space-y-1">
            {sessions.map((session) => (
              <button
                type="button"
                key={session.id}
                onClick={() => setSelectedSessionId(session.id)}
                className={`w-full rounded-lg px-3 py-2.5 text-left transition-colors ${
                  session.id === selectedSessionId
                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-950/40 dark:text-primary-300'
                    : 'text-content-secondary hover:bg-surface-raised hover:text-content-primary'
                }`}
              >
                <span className="block truncate text-sm font-medium">{session.title}</span>
                <span className="mt-1 block text-xs opacity-70">{formatConversationDate(session.last_activity_at)}</span>
              </button>
            ))}
          </div>
        </nav>
      </aside>

      <section className="flex min-w-0 flex-1 flex-col bg-surface-page">
        <header className="flex items-center justify-between border-b border-border-default bg-surface-base px-4 py-3">
          <div className="flex items-center gap-3">
            {!sidebarOpen && (
              <button type="button" onClick={() => setSidebarOpen(true)} className="rounded-lg p-2 text-content-secondary hover:bg-surface-raised hover:text-content-primary" aria-label="Show session history">
                <PanelLeftOpen className="h-4 w-4" />
              </button>
            )}
            <div className="flex items-center gap-2">
              <div className="rounded-lg bg-primary-50 p-2 text-primary-600 dark:bg-primary-950/30 dark:text-primary-400"><Bot className="h-4 w-4" /></div>
              <div>
                <h1 className="text-sm font-semibold text-content-primary">EZ AP Assistant</h1>
                <p className="text-xs text-content-muted">Conversation stays here. Platform results appear as cards.</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden items-center rounded-lg border border-border-default bg-surface-raised p-0.5 text-xs font-medium sm:flex">
              <Link to="/" className="rounded-md px-2.5 py-1.5 text-content-secondary hover:text-content-primary">Platform</Link>
              <span className="rounded-md bg-surface-base px-2.5 py-1.5 text-primary-700 shadow-sm dark:text-primary-300">Chat</span>
            </div>
            <Link to="/" className="btn-ghost !px-2 sm:hidden" aria-label="Return to platform"><ChevronLeft className="h-4 w-4" /></Link>
            <UserMenu />
          </div>
        </header>

        <main className="mx-auto flex min-h-0 w-full max-w-5xl flex-1 flex-col">
          {history.isLoading && messages.length === 0 ? (
            <div className="flex flex-1 items-center justify-center text-sm text-content-muted">Loading conversation…</div>
          ) : (
            <MessageList
              messages={messages}
              onConfirm={handleConfirm}
              onCancel={handleCancel}
              confirmLoadingId={confirmMutation.isPending ? String(confirmMutation.variables) : null}
              resolvedIds={resolvedConfirmationIds}
              onSendSuggestion={handleSend}
            />
          )}
          <div className="border-t border-border-default bg-surface-base px-3 py-3 sm:px-6">
            <ChatInput onSend={handleSend} disabled={isStreaming || !selectedSessionId} />
          </div>
        </main>
      </section>
      <ToastContainer />
    </div>
  );
};
